import { CANVAS_SIZE } from "@/features/draw/constants/canvas";
import type { ColorStats, Layer } from "@/features/draw/types";
import { isValidLayer } from "@/features/draw/utils/layer/layerUtils";
import { useColorManagement } from "./useColorManagement";
import { ProcessedImage, useImageProcessing } from "./useImageProcessing";

interface ExportOptions {
  layers: Layer[];
  getUniqueColors: (layers: Layer[]) => string[];
  getColorStatistics: (layers: Layer[]) => ColorStats;
}

interface ImportOptions {
  selectedLayerId: number;
  layers: Layer[];
  onGenerateLayer: (data: {
    layerId: number;
    pixels: Uint32Array;
    forceAddToHistory?: boolean;
  }) => void;
  onMergeColors: (layers: Layer[], forceAddToHistory: boolean) => void;
}

export function useFileHandling() {
  const { createImageFromFile } = useImageProcessing();

  /**
   * Handle image upload and processing
   * @param event - File input event
   * @param options - Options for image upload
   * @returns Promise with processed image data
   */
  const handleImageUpload = async (event: Event, options: ImportOptions) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) return;

    try {
      return await handleImageFile(file, options);
    } catch (error) {
      console.error("Image upload error:", error);
      alert(
        "Error loading image: " +
        (error instanceof Error ? error.message : "Unknown error")
      );
    } finally {
      // Clear file input
      target.value = "";
    }
  };

  const handleImageFile = async (
    file: File,
    options: ImportOptions
  ): Promise<ProcessedImage> => {
    const processedImage = await createImageFromFile(file);

    // Update the currently selected layer
    options.onGenerateLayer({
      layerId: options.selectedLayerId,
      pixels: processedImage.pixels,
      forceAddToHistory: true,
    });

    return processedImage;
  };

  /**
   * Handle data import from JSON file
   * @param event - File input event
   * @param onImportData - Callback for imported data
   */
  const handleDataImport = async (
    event: Event,
    onImportData: (layers: Layer[]) => void
  ) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) return;

    try {
      const reader = new FileReader();
      const fileContent = await new Promise<string>((resolve, reject) => {
        reader.onload = (e) => {
          const result = e.target?.result;
          if (typeof result !== "string") {
            reject(new Error("Invalid file content"));
            return;
          }
          resolve(result);
        };
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsText(file);
      });

      const importedData = JSON.parse(fileContent);

      // Validate data format
      if (
        !importedData.version ||
        !importedData.layers ||
        !Array.isArray(importedData.layers)
      ) {
        throw new Error("Invalid data format");
      }

      // Validate each layer
      importedData.layers.forEach((layer: unknown) => {
        if (!isValidLayer(layer)) {
          throw new Error(`Invalid layer data`);
        }
      });

      onImportData(importedData.layers as Layer[]);
      alert("Data imported successfully!");
    } catch (error) {
      console.error("Data import error:", error);
      alert(
        `Import failed: ${error instanceof Error ? error.message : "Unknown error"
        }`
      );
    } finally {
      target.value = "";
    }
  };

  /**
   * Export canvas data to JSON file
   * @param options - Export options
   */
  const exportData = (options: ExportOptions) => {
    // Collect all layer data
    const exportData = {
      version: "1.0",
      timestamp: new Date().toISOString(),
      canvas: {
        width: CANVAS_SIZE,
        height: CANVAS_SIZE,
      },
      layers: options.layers.map((layer) => ({
        id: layer.id,
        name: layer.name,
        visible: layer.visible,
        pixels: layer.pixels,
        requiresPrevious: layer.requiresPrevious,
      })),
      statistics: {
        uniqueColors: options.getUniqueColors(options.layers),
        colorUsage: options.getColorStatistics(options.layers),
      },
    };

    // Create download link
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);

    // Create download element
    const link = document.createElement("a");
    link.href = url;
    link.download = `avatar-data-${new Date().toISOString().slice(0, 10)}.json`;

    // Trigger download
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return {
    handleImageUpload,
    handleImageFile,
    handleDataImport,
    exportData,
  };
}
